import { SocketEvents } from "@/api";
import { messageSchema } from "@/controllers/channel/schema";
import { useWsStore } from "@/providers/ws-provider";
import { ApiService } from "@/services/api/api-service";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { ChatMessagesLatestMessageQuery } from "./messages/use-chat-messages-latest-message-query";
import { ChatMessagesQuery } from "./messages/use-chat-messages-query";
import { ChatChannelsQuery } from "./use-channels-query";

type ChatSocketProps = {
  addKey: string;
  updateKey: string;
  queryKey: string;
};

export namespace ChatSocketQuery {
  export const keys = {
    addMessageKey: (channelId: string) => SocketEvents.CHAT_MESSAGE(channelId),
    updateMessageKey: (channelId: string) =>
      SocketEvents.CHAT_MESSAGE_UPDATE(channelId),
    queryKey: (channelId: string) => SocketEvents.CHAT_CHANNEL(channelId),

    all: (channelId: string) =>
      (({
        addKey: keys.addMessageKey(channelId),
        updateKey: keys.updateMessageKey(channelId),
        queryKey: keys.queryKey(channelId)
      }) as const),
  } as const;

  export function getChannelIdFromQueryKey(queryKey: string) {
    return queryKey.split(":")[1];
  }

  export function useQuery({ addKey, updateKey, queryKey }: ChatSocketProps) {
    const { isConnected } = useWsStore();
    const queryClient = useQueryClient();

    React.useEffect(() => {
      const socket = useWsStore.getState().socket;
      if (!socket || !isConnected) {
        return;
      }
      socket.on(addKey, (payload: unknown) => {
        const response = ApiService.parseApiResponse(payload, messageSchema);
        if (response.status === "error") {
          return null;
        }

        queryClient.setQueryData<InfiniteData<ChatMessagesQuery.TData>>(
          [queryKey],
          (oldData) => {
            if (!oldData || !oldData.pages || oldData.pages.length === 0) {
              return oldData;
            }

            const firstPage = oldData.pages[0];
            const lastPage = oldData.pages[oldData.pages.length - 1];
            let pages = structuredClone(oldData.pages);

            if (lastPage.length < firstPage.length) {
              pages[0].unshift(response.data);
            } else {
              pages = [[response.data], ...pages];
            }

            // Update readBy status of the new message
            return {
              ...oldData,
              pages: pages.map((msgs) => {
                return msgs.map((msg) => {
                  return {
                    ...msg,
                    readBy: msg.readBy.map((r) => {
                      if (r.userId !== response.data.user.id) {
                        return r;
                      }

                      return {
                        ...r,
                        readAt: new Date(),
                      };
                    }),
                  };
                });
              }),
            };
          },
        );

        // Keep track of latest message such that we can display it in the chat list
        const channelId = getChannelIdFromQueryKey(queryKey);
        if (!channelId) {
          return;
        }
        ChatMessagesLatestMessageQuery.setData(channelId, response.data);
        ChatChannelsQuery.updateData(
          ChatChannelsQuery.updaters.updateLatestMessage(
            channelId,
            response.data,
          ),
        );
      });

      socket.on(updateKey, (payload: unknown) => {
        const response = ApiService.parseApiResponse(payload, messageSchema);

        if (response.status === "error") {
          return null;
        }

        queryClient.setQueryData<InfiniteData<ChatMessagesQuery.TData>>(
          [queryKey],
          (oldData) => {
            if (!oldData || !oldData.pages || oldData.pages.length === 0) {
              return oldData;
            }

            const updatedPages = oldData.pages.map((page) =>
              page.map((message) => {
                if (message.id === response.data.id) {
                  return response.data;
                }

                return message;
              }),
            );

            return {
              ...oldData,
              pages: updatedPages,
            };
          },
        );
      });

      return () => {
        socket.off(addKey);
        socket.off(updateKey);
      };
    }, [queryClient, addKey, queryKey, updateKey, isConnected]);
  }
}
